import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MainLayout } from "../layout/mainLayout";
import TextField from "@mui/material/TextField";
import Grid2 from "@mui/material/Unstable_Grid2";
import {
    Autocomplete,
    AutocompleteRenderInputParams,
    Button,
} from "@mui/material";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Box from "@mui/material/Box";
import {
    getEmployeeGroupItems,
    getFunctionSettings,
    saveFunctionSettings,
} from "../../services/employeeGroupService";
import { showApiError } from "../framework/formUtils";
import { EmployeeGroupItem } from "../../models/employeeGroup/employeeGroupItem";
import { TESCalculationParameters } from "../../models/calculation/TESCalculationParameters";
import * as store from "../../framework/customStore";
import * as storeActions from "../../models/store/storeActions";
import i18n from "i18next";
import * as CustomStore from "../../framework/customStore";
import * as StoreActions from "../../models/store/storeActions";
import { CalcOvertimes } from "./CalcOvertimes";
import { CalcDailyAllowance } from "./CalcDailyAllowance";
import { LoadingIndicator } from "../framework/loadingIndicatorNew";
import { CalcFillingHours } from "./CalcFillingHours";
import { OwnerParameters } from "../../models/owner/ownerParameters";
import { CalcAdditions } from "./CalcAdditions";
import { CalcShiftSettings } from "./CalcShiftSettings";

type optionTp = { label: string; id: string };

export interface CalcGeneralProps {
    translate?: (str: string) => string;
    handleChange?: (attr: string, value: any) => void;
    settings?: TESCalculationParameters;
    setSettings?: (
        value: React.SetStateAction<TESCalculationParameters>
    ) => void;
}

export const CalculationMain = () => {
    const { t } = useTranslation();
    const [settings, setSettings] = useState<TESCalculationParameters>();
    const [employeeGroups, setEmployeeGroups] = useState<optionTp[]>([]);
    const [employeeGroup, setEmployeeGroup] = useState<optionTp>({
        label: "",
        id: "",
    });
    const [activeTab, setActiveTab] = useState(0);
    const [saveButtonDisabled, setSaveButtonDisabled] = useState(false);
    const [searchIsOn, setSearchIsOn] = useState(true);
    const [started, setStarted] = useState(false);

    const ownerParameters = new OwnerParameters();

    const {
        overtimePeriodBoundaries: overtimeBoundaries,
        overtimeWeekBoundaries,
        overtimeDailyBoundaries,
        overtimeSaturdayBoundaries,
        dailyAllowanceBoundaries,
        isMonthlyOvertimeCalculationOn,
        isSaturdayCalculationOn,
        saturdayStartTime,
        saturdayEndTime,
    } = settings || {};

    const handleChange = (attr: string, value) => {
        setSettings({ ...settings, [attr]: value });
    };

    useEffect(() => {
        if (!started) {
            getEmployeeGroupItems(9999, 1, "", "Name", true)
                .then((response) => {
                    fillEmployeeGroups(response.items);
                    setStarted(true);
                })
                .catch(showApiError);
        }
    }, []);

    useEffect(() => {
        onEmployeeChanged(employeeGroup);
    }, [employeeGroup]);

    const fillEmployeeGroups = (items: EmployeeGroupItem[]) => {
        const groups = items.map((x) => ({ label: x.name, id: x.id }));
        setEmployeeGroups(groups);
        setEmployeeGroup(groups[0]);
    };

    const onEmployeeChanged = (value: optionTp) => {
        setSaveButtonDisabled(true);
        setSearchIsOn(true);

        if (value?.id) {
            getFunctionSettings(value.id)
                .then((response) => {
                    setSettings(response);
                })
                .catch(showApiError)
                .finally(() => {
                    setSaveButtonDisabled(false);
                    setSearchIsOn(false);
                });
        }
    };

    const validate = (): boolean => {
        if (
            [
                overtimeBoundaries,
                overtimeWeekBoundaries,
                overtimeDailyBoundaries,
                overtimeSaturdayBoundaries,
                dailyAllowanceBoundaries,
            ].some(
                (b) =>
                    b.length === 2 &&
                    b.find((v) => v.boundary === 50)?.boundaryValue >
                        b.find((v) => v.boundary === 100)?.boundaryValue
            )
        ) {
            CustomStore.customStore.dispatch(
                StoreActions.showErrorMessage(translate("CHECKBOUNDARYVALUE"))
            );
            return false;
        }

        if (isMonthlyOvertimeCalculationOn && overtimeBoundaries.length > 0) {
            CustomStore.customStore.dispatch(
                StoreActions.showErrorMessage(
                    translate("CheckMonthlyAndPeriodOvertime")
                )
            );
            return false;
        }

        if (
            isSaturdayCalculationOn &&
            saturdayEndTime.substring(0, 5) !== "00:00" &&
            saturdayEndTime <= saturdayStartTime
        ) {
            CustomStore.customStore.dispatch(
                StoreActions.showErrorMessage(
                    translate("saturdayEndTimeMustBeAfterStartTime")
                )
            );
            return false;
        }

        return true;
    };

    const saveParameters = () => {
        if (validate()) {
            if (employeeGroup) {
                setSaveButtonDisabled(true);
                setSearchIsOn(true);

                saveFunctionSettings(employeeGroup.id, settings)
                    .then(() => {
                        store.customStore.dispatch(
                            storeActions.showSuccessMessage(
                                t("common.saveSuccess")
                            )
                        );
                    })
                    .catch(() => {
                        store.customStore.dispatch(
                            storeActions.showErrorMessage(
                                translate("unexpectedError")
                            )
                        );
                    })
                    .finally(() => {
                        setSaveButtonDisabled(false);
                        setSearchIsOn(false);
                    });
            }
        }
    };

    const translate = (str: string): string => {
        //i18n using not well done interface so.
        /*eslint-disable   @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call */
        return i18n.t(str);
    };

    return (
        <MainLayout
            topComponent={
                <Grid2
                    container
                    direction="row"
                    paddingLeft={2}
                    alignItems={"center"}
                >
                    <Autocomplete
                        className="field-width-normal"
                        isOptionEqualToValue={(option, value) =>
                            option.id === value.id
                        }
                        renderInput={(
                            params: AutocompleteRenderInputParams
                        ) => (
                            <TextField
                                {...params}
                                label={t("employee.employeeGroup")}
                            />
                        )}
                        onChange={(event, value) => setEmployeeGroup(value)}
                        options={employeeGroups}
                        disableClearable
                        autoSelect
                        disablePortal
                        disabled={searchIsOn}
                        value={employeeGroup}
                    />
                    <Button
                        style={{ marginLeft: "3rem" }}
                        id="save_button"
                        variant="save"
                        onClick={() => saveParameters()}
                        key="btn-save"
                        disabled={saveButtonDisabled}
                    >
                        {t("common.save")}
                    </Button>
                </Grid2>
            }
        >
            <>
                <LoadingIndicator loading={searchIsOn} />
                {!settings ? null : (
                    <Box>
                        <Tabs
                            value={activeTab}
                            onChange={(_, v: number) => setActiveTab(v)}
                            variant="scrollable"
                            scrollButtons="auto"
                        >
                            <Tab
                                label={translate("workShifts")}
                                id="calc-tab-shifts"
                            />
                            <Tab
                                label={translate("additions")}
                                id="calc-tab-daily-additions"
                            />
                            <Tab
                                label={translate("periodOvertimes")}
                                id="calc-tab-overtimes"
                            />
                            <Tab
                                label={translate("dailyAllowances")}
                                id="calc-tab-daily-allowance"
                            />
                            {ownerParameters.getOptionOwnerHasFillingHoursCalcSettingEnabled() && (
                                <Tab
                                    label={translate("fillingHours")}
                                    id="calc-tab-filling-hours"
                                />
                            )}
                        </Tabs>
                        <Box sx={{ pt: 2 }}>
                            {activeTab === 0 && (
                                <CalcShiftSettings
                                    translate={translate}
                                    handleChange={handleChange}
                                    settings={settings}
                                    setSettings={setSettings}
                                />
                            )}
                            {activeTab === 1 && (
                                <CalcAdditions
                                    translate={translate}
                                    handleChange={handleChange}
                                    settings={settings}
                                />
                            )}
                            {activeTab === 2 && (
                                <CalcOvertimes
                                    translate={translate}
                                    handleChange={handleChange}
                                    settings={settings}
                                    setSettings={setSettings}
                                />
                            )}
                            {activeTab === 3 && (
                                <CalcDailyAllowance
                                    translate={translate}
                                    settings={settings}
                                    setSettings={setSettings}
                                />
                            )}
                            {activeTab === 4 &&
                                ownerParameters.getOptionOwnerHasFillingHoursCalcSettingEnabled() && (
                                    <CalcFillingHours
                                        translate={translate}
                                        handleChange={handleChange}
                                        settings={settings}
                                    />
                            )}
                        </Box>
                    </Box>
                )}
            </>
        </MainLayout>
    );
};
